import { ChunkDataProvider, SceneId } from '../api';
import { getChunkKey } from '../chunkUtils';
import { TerrainType } from '../data/TerrainType';

/**
 * Service for querying terrain information from the map.
 * Shared between client and server to allow for both visual effects (client)
 * and physics/gameplay modifications (server).
 */
export class TerrainService {
  /**
   * Get the terrain type at a specific world position.
   *
   * It iterates through all layers in the "Ground" group from top to bottom
   * and returns the Terrain property of the first non-empty tile it finds.
   *
   * @param provider - Interface for accessing chunk and scene metadata
   * @param sceneId - The scene context for the lookup
   * @param worldX - World X position in pixels
   * @param worldY - World Y position in pixels
   * @returns The resolved TerrainType, or undefined if no terrain is found
   */
  public getTerrainAt(
    provider: ChunkDataProvider,
    sceneId: SceneId,
    worldX: number,
    worldY: number,
  ): TerrainType | undefined {
    const sceneMetadata = provider.getSceneMetadata(sceneId);
    const chunks = provider.getChunks(sceneId);

    if (!sceneMetadata || !chunks) {
      return undefined;
    }

    const { tileWidth, tileHeight, chunkSize, tilesets, layerGroups } = sceneMetadata;

    // Calculate tile coordinates
    const col = Math.floor(worldX / tileWidth);
    const row = Math.floor(worldY / tileHeight);

    // Calculate chunk coordinates
    const chunkX = Math.floor(col / chunkSize);
    const chunkY = Math.floor(row / chunkSize);
    const chunkKey = getChunkKey(chunkX, chunkY);

    const chunkData = chunks.get(chunkKey);
    if (!chunkData) {
      return undefined;
    }

    // Local coordinates within the chunk.
    // JS % operator can return negative values, so we use the modulo formula
    // ((n % m) + m) % m to ensure a positive index even if world coordinates are negative.
    const adjustedLocalCol = ((col % chunkSize) + chunkSize) % chunkSize;
    const adjustedLocalRow = ((row % chunkSize) + chunkSize) % chunkSize;
    const finalLocalIndex = adjustedLocalRow * chunkSize + adjustedLocalCol;

    // Get layers in the "Ground" group
    const groundLayers = Object.keys(chunkData.layers).filter(
      (layerName) => layerGroups?.[layerName] === 'Ground',
    );

    // Iterate top-down (reverse insertion order which is top-to-bottom in TMX)
    for (let i = groundLayers.length - 1; i >= 0; i--) {
      const layerName = groundLayers[i];
      const layerData = chunkData.layers[layerName];
      const tileId = layerData[finalLocalIndex];

      if (tileId !== 0 && tileId !== undefined) {
        // Find tileset
        const tileset = tilesets.find(
          (ts) => tileId >= ts.firstgid && tileId < ts.firstgid + ts.tilecount,
        );

        if (tileset?.terrain) {
          const localId = tileId - tileset.firstgid;
          const terrain = tileset.terrain[localId];
          if (terrain) {
            return terrain;
          }
        }
      }
    }

    return undefined;
  }
}
